import React from 'react';
import {
  Brain,
  Activity,
  MessageSquare,
  Database,
  Search,
  Calculator,
  CheckSquare,
  BookOpen,
  User,
  FileText,
  Edit3,
  Loader,
  RefreshCw,
} from 'lucide-react';
import styles from '@/styles/components/process-progress.module.css';

/**
 * Step configuration with labels and icons.
 * Includes canonical steps and dynamic tool-based steps from Celery progress.
 */
const STEP_CONFIG = {
  // Canonical steps (emitted directly by WebSocket handler)
  thinking: { icon: Brain },
  gathering_information: { icon: Database },
  analyzing: { icon: Activity },
  searching_loan_info: { icon: Search },
  performing_calculation: { icon: Calculator },
  reviewing_results: { icon: CheckSquare },
  reading_documents: { icon: BookOpen },
  preparing_response: { icon: MessageSquare },

  // Dynamic steps from Celery worker progress
  analyzing_request: { icon: Brain },
  initializing: { icon: Loader },
  responding: { icon: MessageSquare },

  // Tool-based steps (step IDs use "tool:" prefix from backend)
  'tool:get_user_details': { icon: User },
  'tool:get_borrower_details': { icon: User },
  'tool:contact_client_admin': { icon: MessageSquare },
  'tool:contact_branch_admin': { icon: MessageSquare },
  'tool:contact_loan_officer': { icon: MessageSquare },
  'tool:doc_data': { icon: FileText },
  'tool:doc_data_b2b': { icon: FileText },
  'tool:doc_data_income': { icon: FileText },
  'tool:doc_data_all': { icon: FileText },
  'tool:get_doc_consumer_data': { icon: FileText },
  'tool:get_doc_list': { icon: FileText },
  'tool:list_doc': { icon: FileText },
  'tool:get_report': { icon: FileText },
  'tool:income_calculations': { icon: Calculator },
  'tool:calculations': { icon: Calculator },
  'tool:pi_calculator': { icon: Calculator },
  'tool:product_matcher': { icon: Search },
  'tool:accurate_product_matcher': { icon: Search },
  'tool:fha_loan_limits': { icon: Search },
  'tool:LoanLimitTool': { icon: Search },
  'tool:IncomeLimitTool': { icon: Search },
  'tool:update_personal_details': { icon: Edit3 },
  'tool:update_borrower_address': { icon: Edit3 },
  'tool:update_loan_info': { icon: Edit3 },
  'tool:update_employment_profiles': { icon: Edit3 },
  'tool:update_employment_records': { icon: Edit3 },
  'tool:update_borrower_assets': { icon: Edit3 },
  'tool:update_borrower_liabilities': { icon: Edit3 },
  'tool:update_borrower_real_estate': { icon: Edit3 },
  'tool:detailed_review_agent': { icon: Activity },
  // The same agent under its name before the rename; drop once no environment runs it.
  'tool:pre_underwriting_agent': { icon: Activity },
  'tool:income_calculation_agent': { icon: Calculator },
  'tool:disclosures_agent': { icon: CheckSquare },
  'tool:rag_tool': { icon: Search },
  'tool:rag_search': { icon: Search },
  'tool:update_to_borrower_id': { icon: User },
  'tool:assign_borrower_id': { icon: User },
  'tool:borrower_applications': { icon: Search },
};

/**
 * ProcessProgress Component with Animated Light Wave on Text
 *
 * Displays current AI processing step with icon and wave effect.
 * Supports both canonical steps and dynamic tool-based steps
 * streamed from the Celery worker via progress updates.
 */
const ProcessProgress = ({ processState }) => {
  if (!processState || processState.status === 'idle') {
    return null;
  }

  const { steps = [], status } = processState;

  // Find the currently active step
  const activeStep = steps.find((s) => s.status === 'active');

  // If complete, don't show anything (response bubble will appear)
  if (status === 'complete' && !activeStep) {
    return null;
  }

  // Get the step config — use the label from the backend event (dynamic),
  // fall back to a generic label only if none was provided.
  const stepConfig = activeStep ? STEP_CONFIG[activeStep.id] : null;
  const label = activeStep?.label || 'Processing';
  const IconComponent = stepConfig?.icon || Activity;

  return (
    <div className={styles.container}>
      <IconComponent size={16} className={styles.icon} />
      <span className={styles.statusText}>{label}</span>
    </div>
  );
};

export default ProcessProgress;
